// CQ water treatment-train recommendation engine - JavaScript twin of
// pipeline/recommend.py. Data tables come from engine_data.js (generated from
// Python). Parity with Python is enforced by recommend.test.js against
// fixtures.json. Keep this in sync by editing recommend.py then re-running
// pipeline/export_engine_data.py - do NOT hand-edit the data here.
(function (root) {
  "use strict";

  // Unit lookups are CASE-INSENSITIVE (state portals emit UG/L / MG/L / NG/L /
  // PCI/L uppercase; exact matching mis-converted 112k+ values by 1000x).
  function convertToMgl(value, unit) {
    const val = parseFloat(value);
    if (isNaN(val)) return null;
    const f = { "ppb": 0.001, "ug/l": 0.001, "µg/l": 0.001,
      "ppt": 1e-6, "ng/l": 1e-6, "ppm": 1.0, "mg/l": 1.0, "g/l": 1000.0 };
    const k = (unit || "mg/L").trim().toLowerCase();
    if (!(k in f)) return null; // fail closed: never treat an unknown unit as mg/L
    return val * f[k];
  }

  function convertHardness(value, unit) {
    const val = parseFloat(value);
    if (isNaN(val)) return null;
    const f = { "gpg": 1.0, "mg/l": 0.0584, "ppm": 0.0584, "mmol/l": 5.847,
      "dgh": 1.04, "fh": 0.584 };
    const k = (unit || "gpg").trim().toLowerCase();
    if (!(k in f)) return null; // fail closed
    return val * f[k];
  }

  function convertRadioactivity(value, unit, cid) {
    const val = parseFloat(value);
    if (isNaN(val)) return null;
    const u = (unit || "pCi/L").trim().toLowerCase();
    if (cid === "gross_beta" && u === "mrem/yr") return val * 10;
    const f = { "pci/l": 1.0, "bq/l": 27.027, "mbq/l": 0.027027 };
    if (!(u in f)) return null; // fail closed
    return val * f[u];
  }

  function stage(number, name, opts) {
    opts = opts || {};
    const s = { number: number, name: name, type: opts.type || "",
      size: opts.size || "Standard", reasons: opts.reasons || [] };
    if (opts.note) s.note = opts.note;
    if (opts.alternative) s.alternative = opts.alternative;
    return s;
  }

  function roSizing(people) {
    let p = parseInt(people, 10);
    if (isNaN(p)) p = 4;
    if (p <= 2) return "300-500 GPD";
    if (p <= 4) return "500-750 GPD";
    if (p <= 6) return "750-1000 GPD";
    return "1500+ GPD";
  }

  function recommend(profile, context, data) {
    context = context || {};
    data = data || root.ENGINE_DATA;
    const ZT = data.ZERO_TOLERANCE, TH = data.THRESHOLDS,
      ZTF = data.ZT_FLOORS || {},
      SPECIALTY_MAP = data.SPECIALTY_MAP, SPECIALTY_SYSTEMS = data.SPECIALTY_SYSTEMS;
    const source = context.source || "city";

    const needed = new Set();
    const reasonsBySystem = {};
    const summary = [];
    const trace = [];
    let pfasDetected = false, hardnessGpg = 0, phValue = 0, tdsMgl = 0;

    function addSystem(system, contaminant, value, unit, reason) {
      needed.add(system);
      (reasonsBySystem[system] = reasonsBySystem[system] || [])
        .push({ contaminant: contaminant, value: value, unit: unit, reason: reason });
    }

    // zero-tolerance
    for (const e of profile) {
      const cid = e.canonical_id;
      if (!(cid in ZT)) continue;
      const unit = e.unit || "mg/L";
      // unit-agnostic positive test: any detection triggers, whether the lab
      // reported mass (ug/L) or activity (pCi/L)
      const rawv = parseFloat(e.value);
      if (isNaN(rawv) || rawv <= 0) continue;
      const z = ZT[cid];
      const floor = ZTF[cid];
      let meaningful = true;
      if (floor) {
        const passes = function (kind, v) {
          if (kind === "mass") { const mv = convertToMgl(e.value, unit); return mv === null ? null : mv >= v; }
          if (kind === "activity") { const av = convertRadioactivity(e.value, unit, cid); return av === null ? null : av >= v; }
          return rawv >= v;
        };
        meaningful = passes(floor.kind, floor.v);
        if (meaningful === null && floor.alt_kind) meaningful = passes(floor.alt_kind, floor.alt_v);
        meaningful = !!meaningful;
      }
      if (meaningful) {
        addSystem(z.system, z.name, e.value, unit, "Above the Crystal Quest action level");
        summary.push({ name: z.name, value: e.value, unit: unit, status: "detected", note: "Zero tolerance" });
        if (z.system === "pfas") pfasDetected = true;
      } else {
        trace.push({ contaminant: z.name, canonical_id: cid, value: e.value, unit: unit });
        summary.push({ name: z.name, value: e.value, unit: unit, status: "trace", note: "Below the CQ action level" });
      }
    }

    // thresholds
    for (const e of profile) {
      const cid = e.canonical_id;
      if (!(cid in TH)) continue;
      const cfg = TH[cid];
      const unit = e.unit || "mg/L";
      const raw = e.value;
      const kind = cfg.kind;

      if (kind === "ph") {
        const ph = parseFloat(raw);
        if (isNaN(ph)) continue;
        phValue = ph;
        if (ph < cfg.threshold_low) {
          addSystem("acidNeutralizer", "Low pH", ph, "pH", "Acidic water can corrode pipes");
          summary.push({ name: "pH", value: ph, unit: "", status: "low", note: "Below 6.5" });
        } else {
          summary.push({ name: "pH", value: ph, unit: "", status: ph > cfg.threshold_high ? "high" : "ok", note: "" });
        }
        continue;
      }
      if (kind === "hardness") {
        const h = convertHardness(raw, unit);
        if (h === null) continue;
        hardnessGpg = h;
        if (h > cfg.threshold) {
          addSystem("softener", "Hardness", raw, unit, "Causes scale buildup");
          summary.push({ name: "Hardness", value: raw, unit: unit, status: "elevated", note: ">" + cfg.threshold + " gpg" });
        }
        continue;
      }
      if (kind === "mineral") {
        const v = convertToMgl(raw, unit);
        if (v === null) continue;
        if (v > cfg.threshold) {
          addSystem("softener", cfg.name, v, unit, "High " + cfg.name.toLowerCase() + " contributes to hardness/scaling");
          summary.push({ name: cfg.name, value: v, unit: unit, status: "elevated", note: ">" + cfg.threshold + " mg/L" });
        }
        continue;
      }
      if (kind === "turbidity") {
        const ntu = parseFloat(raw);
        if (isNaN(ntu)) continue;
        if (ntu > cfg.threshold) {
          addSystem("turbidity", "Turbidity", ntu, "NTU", "Cloudiness/particulates need fine filtration");
          summary.push({ name: "Turbidity", value: ntu, unit: "NTU", status: "elevated", note: ">" + cfg.threshold + " NTU" });
        }
        continue;
      }
      if (kind === "radio") {
        const rv = convertRadioactivity(raw, unit, cid);
        if (rv !== null && rv > cfg.threshold) {
          addSystem(cfg.system, cfg.name, raw, unit, "Radioactive contamination");
          summary.push({ name: cfg.name, value: raw, unit: unit, status: (rv >= cfg.threshold * 2 ? "high" : "elevated"), note: "" });
        }
        continue;
      }
      const converted = convertToMgl(raw, unit);
      if (converted === null) continue;
      if (cid === "tds") tdsMgl = converted;
      if (cfg.any_detection && converted > 0) {
        addSystem(cfg.system, cfg.name, raw, unit, "Any detection warrants treatment");
        summary.push({ name: cfg.name, value: raw, unit: unit, status: "detected", note: "" });
      } else if (converted > cfg.threshold) {
        addSystem(cfg.system, cfg.name, raw, unit, "Exceeds recommended level");
        summary.push({ name: cfg.name, value: raw, unit: unit, status: converted >= cfg.threshold * 2 ? "high" : "elevated", note: "" });
      }
    }

    // bacteria
    const clarity = context.clarity || "clear";
    const coliform = !!context.coliform;
    let ecoli = !!context.ecoli;
    for (const e of profile) {
      if (e.canonical_id === "ecoli") {
        const v = parseFloat(e.value);
        if (!isNaN(v) && v > 0) ecoli = true;
      }
    }
    const coliformCount = context.coliform_count || 0;
    if (coliform || ecoli || coliformCount > 0) {
      let useChlorine = false;
      if (ecoli) useChlorine = true;
      else if (coliformCount > 20) useChlorine = true;
      else if (coliformCount >= 5 && (clarity === "cloudy" || source === "well")) useChlorine = true;
      else if (clarity === "cloudy") useChlorine = true;
      const nm = ecoli ? "E. coli" : "Total Coliform";
      if (useChlorine) addSystem("chlorineInjection", nm, coliformCount || "Present", "", "Provides residual disinfection protection");
      else addSystem("uv", nm, coliformCount || "Present", "", "Destroys 99.99% of bacteria and viruses - chemical-free");
      summary.push({ name: nm, value: "Present", unit: "", status: "detected", note: "Zero tolerance" });
    }

    if (source === "city") addSystem("smart", "City Water", null, "", "Removes chlorine, chemicals, and improves taste");
    else if (context.continuous_chlorination) addSystem("smart", "Well Chlorination", null, "", "Clears chlorine and disinfection byproducts after your chlorinator");

    // RO decision
    const specialtyCount = SPECIALTY_SYSTEMS.filter((s) => needed.has(s)).length;
    const hasRoOnly = needed.has("sodium") || needed.has("chloride");
    // AJ 07-21 canon: these classes route to RO as the most reliable reduction
    const roPreferred = (data.RO_PREFERRED_SYSTEMS || []).some((s) => needed.has(s));
    // AJ ruling 2026-07-29: a microplastics concern routes to reverse osmosis
    // as the main fix (SMART carbon is the acceptable media route). Intent
    // flag only, never a fabricated measurement: no profile row, no summary
    // row, no severity weight.
    const microplasticsIntent = !!context.microplastics_intent;
    const highTds = tdsMgl > 500;
    let useRo = specialtyCount >= 3 || pfasDetected || hasRoOnly || highTds || roPreferred || microplasticsIntent;
    if (context.force_ro !== undefined && context.force_ro !== null) useRo = !!context.force_ro;

    const train = [];
    let n = 1;
    function push(name, opts) { train.push(stage(n, name, opts)); n++; }

    push("Sediment Pre-Filter", { type: "always-included",
      reasons: [{ contaminant: "All Water", value: null, unit: "", reason: "First line of defense - removes particles and sediment" }] });

    // Chlorine injection doses at the front of the train so downstream carbon
    // strips the residual before the tap.
    if (needed.has("chlorineInjection")) push("Chlorine Injection System", { type: "disinfection", size: "Metered Dosing", reasons: reasonsBySystem.chlorineInjection || [] });

    if (useRo) {
      if (needed.has("acidNeutralizer"))
        push("Acid Neutralizer", { type: "pretreatment", reasons: reasonsBySystem.acidNeutralizer || [] });
      if (needed.has("radon"))
        push("Radon Reduction (GAC)", { type: "specialty", reasons: reasonsBySystem.radon || [] });
      if (needed.has("silica"))
        push("Silica Reduction System (RO Pre-Treatment)", { type: "pretreatment",
          reasons: reasonsBySystem.silica || [{ contaminant: "Silica", value: null, unit: "", reason: "Silica must be reduced before RO to prevent membrane fouling and scaling" }] });
      if (needed.has("metal"))
        push("Iron/Metal Removal System (RO Pre-Treatment)", { type: "pretreatment",
          reasons: reasonsBySystem.metal || [{ contaminant: "Metals", value: null, unit: "", reason: "Iron and manganese must be removed to prevent RO membrane fouling" }] });
      if (needed.has("softener") || hardnessGpg > 3)
        push("Water Softener (RO Pre-Treatment)", { type: "pretreatment",
          reasons: reasonsBySystem.softener || [{ contaminant: "Hardness", value: null, unit: "", reason: "Removes hardness minerals to protect RO membranes from scale buildup" }] });

      const roNeedsCarbon = ["smart", "chlorine", "chloramine", "dbp", "voc"].some((s) => needed.has(s));
      if (roNeedsCarbon) {
        let cr = [];
        for (const s of ["smart", "chlorine", "chloramine", "dbp", "voc"]) cr = cr.concat(reasonsBySystem[s] || []);
        if (!cr.length) cr = [{ contaminant: "Chlorine/Chloramine", value: null, unit: "", reason: "Carbon removes chlorine/chloramine before RO to protect the membrane" }];
        push("Built-In Prefilters (Sediment + Carbon Block + SMART)", { type: "always-included", reasons: cr });
      }

      let roReason;
      if (hasRoOnly) roReason = "Required for sodium/chloride removal - only RO can effectively reduce these ions";
      else if (highTds) roReason = "High TDS (" + Math.round(tdsMgl) + " mg/L) requires RO for meaningful reduction";
      else if (pfasDetected) roReason = "PFAS detected - RO provides comprehensive removal of all PFAS chain lengths";
      else if (specialtyCount >= 3) roReason = "More efficient than " + specialtyCount + " individual specialty systems";
      else if (microplasticsIntent) roReason = "Microplastics are particle work - reverse osmosis membranes are far finer than plastic particles";
      else roReason = "Comprehensive removal of 90-99% of dissolved contaminants";

      push("Whole House Reverse Osmosis", { type: "ro-system", size: roSizing(context.people),
        reasons: [{ contaminant: "Combined profile", value: null, unit: "", reason: roReason }] });
      push("Alkalizing Remineralization Filter", { type: "post-treatment",
        reasons: [{ contaminant: "Post-RO", value: null, unit: "", reason: "Restores healthy minerals and balances pH after RO treatment" }] });
    } else {
      if (needed.has("metal"))
        push("Iron/Manganese/Sulfur Removal", { reasons: reasonsBySystem.metal || [] });

      if (needed.has("acidNeutralizer"))
        push("Acid Neutralizer", { reasons: reasonsBySystem.acidNeutralizer || [] });
      if (needed.has("softener"))
        push("Water Softener", { reasons: reasonsBySystem.softener || [] });
      const needsCarbon = ["smart", "chlorine", "chloramine", "dbp", "voc"].some((s) => needed.has(s)) || pfasDetected;
      if (needsCarbon) {
        let cr = [];
        for (const s of ["smart", "chlorine", "chloramine", "dbp", "voc", "pfas"]) cr = cr.concat(reasonsBySystem[s] || []);
        push("SMART Whole House Filter", { reasons: cr });
      }

      const added = new Set();
      for (const key of Object.keys(SPECIALTY_MAP)) {
        if (key === "tds" || key === "sodium" || key === "chloride") continue; // RO-only labels
        const sysname = SPECIALTY_MAP[key];
        if (needed.has(key) && !added.has(sysname)) {
          added.add(sysname);
          push(sysname, { reasons: reasonsBySystem[key] || [] });
        }
      }
      push("Carbon Post-Filter", { type: "always-included",
        reasons: [{ contaminant: "Final Polish", value: null, unit: "", reason: "Ensures crystal clear, great-tasting water" }] });
    }

    if (needed.has("uv")) push("UV Disinfection System", { type: "disinfection", reasons: reasonsBySystem.uv || [] });

    return { train: train, summary: summary, useRo: useRo,
      trace: trace,
      flags: { pfasDetected: pfasDetected, vocDetected: needed.has("voc"), hardnessGpg: hardnessGpg, phValue: phValue,
        tdsMgl: tdsMgl, specialtyCount: specialtyCount, hasRoOnly: hasRoOnly, highTds: highTds,
        has_sodium: needed.has("sodium"), has_chloride: needed.has("chloride"),
        ro_preferred: roPreferred,
        ro_preferred_radionuclide: needed.has("radium") || needed.has("radioactive"),
        microplastics_intent: microplasticsIntent } };
  }

  function attachProducts(rec, data) {
    data = data || root.ENGINE_DATA;
    const MAP = data.PRODUCT_MAP;
    const fallback = { collection: "Whole House Filters",
      product: { name: "SMART Whole House Water Filter", url: "https://crystalquest.com/products/smart-whole-house-water-filter" } };
    const seenUrls = {};
    for (const s of rec.train) {
      const m = MAP[s.name] || fallback;
      s.collection = m.collection;
      s.product = m.product ? { name: m.product.name, url: m.product.url } : {};
      if (s.type === "always-included") s.included = true;
      const url = s.product.url;
      if (url && s.type !== "always-included") {
        if (seenUrls[url]) s.same_product_as = seenUrls[url];
        else seenUrls[url] = s.name;
      }
    }
    return rec;
  }

  const api = { recommend: recommend, attachProducts: attachProducts, convertRadioactivity: convertRadioactivity };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else { root.CQEngine = api; }
})(typeof window !== "undefined" ? window : this);
